import axios from "axios";
import type { OEMProfile } from "../../types/Profiles/oemProfile.types";

const backendUrl =
  process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: `${backendUrl}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const normalizeOEMProfile = (data: any): OEMProfile => {
  return {
    identity: {
      companyName: data?.identity?.companyName || "",
      logo: data?.identity?.logo || "",
      country: data?.identity?.country || "",
      city: data?.identity?.city || "",
      headquarters: data?.identity?.headquarters || "",
      website: data?.identity?.website || "",
      contactEmail: data?.identity?.contactEmail || "",
      contactPhone: data?.identity?.contactPhone || "",
      description: data?.identity?.description || "",
      industry: data?.identity?.industry || "",
      companyType: data?.identity?.companyType || "oem_constructeur",
      aidaCategory: data?.identity?.aidaCategory || "oem_constructeur",
      aidaStatus: data?.identity?.aidaStatus || "inscrit",
      aidaBadge: data?.identity?.aidaBadge || "aida_verified",
      aidaScore: Number(data?.identity?.aidaScore || 0),
      aidaOutOfScope: Number(data?.identity?.aidaOutOfScope || 0),
      aidaOutOfScopeReason: data?.identity?.aidaOutOfScopeReason || "",
    },

    industrialPresence: {
      plants: Number(data?.industrialPresence?.plants || 0),
      assemblyLines: Number(data?.industrialPresence?.assemblyLines || 0),
      productionCapacity: Number(
        data?.industrialPresence?.productionCapacity || 0
      ),
      sopPrograms: Number(data?.industrialPresence?.sopPrograms || 0),
      employeesHired: Number(data?.industrialPresence?.employeesHired || 0),
    },

    procurementSegments: Array.isArray(data?.procurementSegments)
      ? data.procurementSegments
      : [],

    rfqStats: {
      activeRFQ: Number(data?.rfqStats?.activeRFQ || 0),
      suppliersMatched: Number(data?.rfqStats?.suppliersMatched || 0),
      projectsInLocalization: Number(
        data?.rfqStats?.projectsInLocalization || 0
      ),
      sopReached: Number(data?.rfqStats?.sopReached || 0),
    },

    localIntegration: {
      localContentTarget: data?.localIntegration?.localContentTarget || "0%",
    },

    profileCompletion: {
      percentage: Number(data?.profileCompletion?.percentage || 0),
      items: Array.isArray(data?.profileCompletion?.items)
        ? data.profileCompletion.items
        : [],
    },

    timeline: Array.isArray(data?.timeline) ? data.timeline : [],
    history: Array.isArray(data?.history) ? data.history : [],
  };
};

const getCurrentUserId = (): number | null => {
  try {
    const rawUser = localStorage.getItem("user");
    if (!rawUser) return null;

    const user = JSON.parse(rawUser);
    return Number(user?.id_user) || null;
  } catch (error) {
    console.error("Erreur lecture localStorage user:", error);
    return null;
  }
};

/* ===========================
   FETCH OEM PROFILE
=========================== */
export const fetchOEMProfile = async (
  id_user: number | string
): Promise<OEMProfile | null> => {
  try {
    const response = await axiosInstance.get(`/oems/profile-view/${id_user}`, {
      params: { t: Date.now() },
    });
    return normalizeOEMProfile(response.data);
  } catch (error) {
    console.error("Erreur fetchOEMProfile:", error);
    return null;
  }
};

/* ===========================
   FETCH CURRENT OEM PROFILE
=========================== */
export const fetchCurrentOEMProfile = async (): Promise<OEMProfile | null> => {
  const user_id = getCurrentUserId();

  if (!user_id) {
    throw new Error("id_user introuvable dans localStorage");
  }

  return await fetchOEMProfile(user_id);
};

const resolveOEMUserId = (userId?: number | string | null) => {
  const targetUserId = userId || getCurrentUserId();

  if (!targetUserId) {
    throw new Error("id_user introuvable");
  }

  return targetUserId;
};

/* ===========================
   UPDATE COMPANY
=========================== */
export const updateOEMCompany = async (payload: {
  companyName: string;
  industry: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  city: string;
  headquarters: string;
  description: string;
  companyType?: string;
  aidaCategory?: string;
  aidaStatus?: string;
  aidaBadge?: string;
  aidaScore?: number;
  aidaOutOfScope?: number;
  aidaOutOfScopeReason?: string;
}, userId?: number | string | null): Promise<any> => {
  const user_id = resolveOEMUserId(userId);

  const response = await axiosInstance.post("/oems/update-profile", {
    user_id,
    company_name: payload.companyName,
    industry_sector: payload.industry,
    website: payload.website.trim(),
    phone: payload.contactPhone,
    country: payload.country,
    city: payload.city,
    address: payload.headquarters,
    description: payload.description,
    contact_email: payload.contactEmail,
    company_type: payload.companyType,
    aida_category: payload.aidaCategory,
    aida_status: payload.aidaStatus,
    aida_badge: payload.aidaBadge,
    aida_score: payload.aidaScore,
    aida_out_of_scope: payload.aidaOutOfScope,
    aida_out_of_scope_reason: payload.aidaOutOfScopeReason,
  });

  return response.data;
};

/* ===========================
   UPDATE COMPANY + REFRESH
=========================== */
export const updateOEMCompanyAndRefresh = async (payload: {
  companyName: string;
  industry: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  city: string;
  headquarters: string;
  description: string;
  companyType?: string;
  aidaCategory?: string;
  aidaStatus?: string;
  aidaBadge?: string;
  aidaScore?: number;
  aidaOutOfScope?: number;
  aidaOutOfScopeReason?: string;
}): Promise<OEMProfile | null> => {
  await updateOEMCompany(payload);
  return await fetchCurrentOEMProfile();
};

/* ===========================
   UPDATE INDUSTRIAL
=========================== */
export const updateOEMIndustrial = async (payload: {
  plants: number;
  assemblyLines: number;
  productionCapacity: number;
  sopPrograms: number;
  employeesHired?: number;
}, userId?: number | string | null): Promise<any> => {
  const user_id = resolveOEMUserId(userId);

  const response = await axiosInstance.post("/oems/update-industrial", {
    user_id,
    plants: payload.plants,
    assembly_lines: payload.assemblyLines,
    production_capacity: payload.productionCapacity,
    sop_programs: payload.sopPrograms,
    employees_hired: payload.employeesHired || 0,
  });

  return response.data;
};

/* ===========================
   UPDATE INDUSTRIAL + REFRESH
=========================== */
export const updateOEMIndustrialAndRefresh = async (payload: {
  plants: number;
  assemblyLines: number;
  productionCapacity: number;
  sopPrograms: number;
  employeesHired?: number;
}): Promise<OEMProfile | null> => {
  await updateOEMIndustrial(payload);
  return await fetchCurrentOEMProfile();
};

/* ===========================
   UPDATE PROCUREMENT
=========================== */
export const updateOEMProcurement = async (payload: {
  procurementSegments: string[];
  localContentTarget: number;
}, userId?: number | string | null): Promise<any> => {
  const user_id = resolveOEMUserId(userId);

  const response = await axiosInstance.post("/oems/update-procurement", {
    user_id,
    procurement_segments: payload.procurementSegments,
    local_content_target: `${payload.localContentTarget}%`,
  });

  return response.data;
};

/* ===========================
   UPDATE PROCUREMENT + REFRESH
=========================== */
export const updateOEMProcurementAndRefresh = async (payload: {
  procurementSegments: string[];
  localContentTarget: number;
}): Promise<OEMProfile | null> => {
  await updateOEMProcurement(payload);
  return await fetchCurrentOEMProfile();
};

/* ===========================
   UPLOAD OEM LOGO
=========================== */
export const uploadOEMLogo = async (
  file: File,
  userId?: number | string | null
): Promise<any> => {
  const user_id = resolveOEMUserId(userId);

  const formData = new FormData();
  formData.append("logo", file);
  formData.append("user_id", String(user_id));

  const response = await axiosInstance.post(
    "/oems/update-profile",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

/* ===========================
   UPLOAD OEM LOGO + REFRESH
=========================== */
export const uploadOEMLogoAndRefresh = async (
  file: File,
  userId?: number | string | null
): Promise<OEMProfile | null> => {
  const targetUserId = resolveOEMUserId(userId);
  await uploadOEMLogo(file, targetUserId);
  return await fetchOEMProfile(targetUserId);
};

export default axiosInstance;
